import { API } from '@/shared/plugins/Api/API'
import { fetchAllPages } from '@/shared/plugins/Api/APPSERVICE/v3/cursorPagination'
import Config from '@/shared/Config'

// The DM custom sources API. Its contract is docs/handbooks/custom-sources-api.md
// in dm-control-plane. Pages find a connector by its `id`; the API only takes its `name`.

const BASE = 'public/v1/customSource'
// The backend's largest page, so a project rarely needs a second call.
const PAGE_SIZE = 100

// A name can come from the page address: encoded, it stays one path segment.
function pathOf (name) {
  return `${BASE}/${encodeURIComponent(name)}`
}

function mainFile (name) {
  return `fp${name}/fp${name}.py`
}

function toField (param) {
  const field = { key: param.key, type: param.type || 'text', required: !!param.required }
  if (param.helper !== undefined) field.helper = param.helper
  if (param.default !== undefined) field.default = param.default
  if (param.choices !== undefined) field.options = param.choices
  if (param.label) field.label = param.label
  return field
}

function fromLight (doc) {
  return {
    id: doc.id,
    name: doc.name,
    displayName: doc.displayName,
    description: doc.description || '',
    type: 'api',
    state: doc.state,
    currentVersion: doc.currentVersion,
    hasDraft: !!doc.hasDraft,
    iconUrl: doc.iconUrl,
    createdAt: doc.createdAt,
    createdBy: doc.createdBy,
    updatedAt: doc.updatedAt,
    deletedAt: doc.deletedAt
  }
}

function fromRevision (name, revision) {
  const definition = revision.definition || {}
  const files = revision.files || {}
  return {
    baseUrl: definition.baseUrl || '',
    auth: definition.auth || { type: 'none' },
    className: (revision.manifest || {}).className,
    options: (definition.credentials || []).map(toField),
    endpoints: (definition.endpoints || []).map(ep => ({
      key: ep.key,
      options: (ep.parameters || []).map(toField),
      pagination: ep.pagination
    })),
    code: files[mainFile(name)] || ''
  }
}

// The live version: what sources run and what Export downloads. A pending draft
// (from the MCP, say) only shows when nothing was published yet.
function fromDetail (doc) {
  return { ...fromLight(doc), ...fromRevision(doc.name, doc.published || doc.draft || {}) }
}

// What the backend reads in an uploaded package, before anything is stored.
function fromValidation (report) {
  const english = ((report.manifest || {}).storeI18n || {}).en || {}
  return {
    ...fromRevision(report.name, report),
    name: report.name,
    displayName: english.name || report.name,
    exists: !!report.exists,
    checks: report.checks || []
  }
}

// The .tgz goes as the agent built it: the backend reads it, checks it and keeps
// everything in it, the image included.
function packageForm (file) {
  const form = new FormData()
  form.append('archive', file)
  return form
}
const MULTIPART = { 'Content-Type': 'multipart/form-data' }

// iconUrl is relative to the DM root; an <img> needs the full URL. The URL never
// changes, so the last update is added to it: a replaced image is fetched again
// instead of the one the page already shows.
async function withIcon (def) {
  if (!def.iconUrl) return { ...def, icon: null }
  const dm = String((await Config()).DM || '').replace(/\/$/, '')
  return { ...def, icon: `${dm}${def.iconUrl}?v=${encodeURIComponent(def.updatedAt || '')}` }
}

class CustomSources extends API {
  // Without a state, every connector but the deleted ones.
  async list ({ state } = {}) {
    const params = state ? { limit: PAGE_SIZE, state } : { limit: PAGE_SIZE }
    const items = await fetchAllPages(this, { url: BASE, params })
    return Promise.all((items || []).map(doc => withIcon(fromLight(doc))))
  }

  // Store-format metas of the published version, read by the standard source form.
  async metas (name) {
    return this.request({ method: 'GET', url: `${pathOf(name)}/metas` })
  }

  // Reads and checks a package. Nothing is stored.
  async validate (file) {
    return fromValidation(await this.request({
      method: 'POST',
      url: `${BASE}/validate`,
      data: packageForm(file),
      headers: MULTIPART,
      retries: 0
    }))
  }

  async get (name) {
    return withIcon(fromDetail(await this.request({ method: 'GET', url: pathOf(name) })))
  }

  // An upload is published right away: the package becomes the draft, which is
  // published at once. It creates the connector, or replaces an existing one.
  async publishPackage (name, file) {
    await this.request({ method: 'PUT', url: `${pathOf(name)}/draft`, data: packageForm(file), headers: MULTIPART, retries: 0 })
    try {
      await this.request({ method: 'POST', url: `${pathOf(name)}/publish`, data: {}, retries: 0 })
    } catch (err) {
      // Leave nothing half done: drop the draft, which also removes a connector
      // that was never published. The publish error is the one to report.
      await this.request({ method: 'DELETE', url: `${pathOf(name)}/draft`, retries: 0 }).catch(() => {})
      throw err
    }
    return this.get(name)
  }

  // Only what the platform edits: the display name, the description and the
  // image (a PNG data URL, or null to remove it). The image is sent only when
  // it changed: `undefined` leaves it as is.
  async update (name, { displayName, description, icon }) {
    const data = { displayName, description: description || '' }
    if (icon !== undefined) data.icon = icon
    await this.request({ method: 'PUT', url: pathOf(name), data, retries: 0 })
    return this.get(name)
  }

  // The backend checks of the version the details page shows (the published one,
  // else the draft): its export, run through validate. Nothing is stored.
  async checks (name) {
    const bytes = await this.exportPackage(name)
    const file = new File([bytes], `${name}.tgz`, { type: 'application/gzip' })
    return (await this.validate(file)).checks
  }

  // The sources built on a connector, which a delete leaves as orphans.
  async dependents (name) {
    return this.request({ method: 'GET', url: `${pathOf(name)}/dependents` })
  }

  // Force only once the user has seen the dependents: without it, the backend
  // refuses to delete a connector that sources still use. Like a source delete,
  // the flag alone confirms: the gateway does not let a browser send a custom header.
  async remove (name, { force = false } = {}) {
    const request = { method: 'DELETE', url: pathOf(name), retries: 0 }
    if (force) request.params = { force: true }
    return this.request(request)
  }

  async exportPackage (name) {
    return this.request({ method: 'GET', url: `${pathOf(name)}/export`, responseType: 'arraybuffer' })
  }

  async skill () {
    return this.request({ method: 'GET', url: `${BASE}/skill`, responseType: 'arraybuffer' })
  }
}

export default CustomSources
export { CustomSources, fromLight, fromDetail, fromValidation }
